import React, {useContext} from "react";
import {useAsync2} from "nate-react-api-helpers";
import {useNavigate} from "react-router-dom";
import {SelectEditor} from "../../../../misc/scroller/SelectEditor";
import {Option} from "../../../../misc/scroller/Table";
import {EditContext} from "../../../../misc/scroller/EditProvider";
import {api} from "../../../../api/API";
import {ScreenSlotOption} from "../../../../api/ElevationTool";
import {useProjectId} from "../../shopdrawing/ProjectName";
import {SnackCancel, useSnackAction} from "../../shopdrawing/hardware/SnackAction";
import {DropdownDataLoading} from "../../settings/DropDowns";
import {useConfirmAdoptIdentity} from "../../shopdrawing/openings/ConfirmAdoptIdentityProvider";

// quote-mode screen (frame) elevation picker — the quote-opening equivalent of the
// shop-drawing PickScreenElevation. Lists the alternative's existing screen elevations;
// selecting one links this quote opening and adopts the screen's identity. The free-solo
// "draw new" option unlinks the opening and opens it in the elevation tool.
export function PickScreenElevation(props: {
    anchor: any;
    row: {id: number};
    width: number;
    alternative: number;
    onCancel(): void;
}) {
    const project = useProjectId();
    const editCtx = useContext(EditContext);
    const navigate = useNavigate();

    // scope the list to this quote opening + alternative so the server hides screens of an
    // incompatible opening type.
    const list = useAsync2((p: number) => api.elevationTool.listScreenElevations({project: p, quoteOpening: props.row.id, alternative: props.alternative}), project, [project, props.alternative]);

    const adoptIdentityConfirm = useConfirmAdoptIdentity();

    // free door slots on other openings' frames (multi-opening screens).
    const slots = useAsync2((p: number) => api.elevationTool.listScreenSlots({project: p, quoteOpening: props.row.id}), project, [project, props.row.id]);

    const fillSlot = useSnackAction(async (slot: ScreenSlotOption) => {
        await api.elevationTool.adoptScreenSlot({project, quoteOpening: props.row.id, primaryOpening: slot.primaryOpening, slot: slot.slot});
        await editCtx.reload(true, [props.row.id]);
        props.onCancel();
    }, {success: "Opening added to frame"});

    const adopt = useSnackAction(async (screen: number) => {
        // an adopt that would overwrite opening properties (handing etc.) returns the diff
        // without saving; prompt (update the opening, or link a private copy when offered),
        // then re-send with the resolution. On cancel nothing was saved.
        const res = await api.elevationTool.adoptScreen({project, quoteOpening: props.row.id, screen});
        if(res.identityChanges?.length) {
            const resolution = await adoptIdentityConfirm.confirm(res.identityChanges, !!res.canCopy);
            if(!resolution) {
                props.onCancel();
                return SnackCancel;
            }
            await api.elevationTool.adoptScreen({project, quoteOpening: props.row.id, screen, confirm: resolution === "update", copy: resolution === "copy"});
        }
        await editCtx.reload(true, [props.row.id]);
        props.onCancel();
    }, {success: "Screen elevation linked"});

    // free-solo: unlink this quote opening's screen elevation, then open the tool, passing the
    // typed name so it auto-saves a fresh screen elevation under it.
    const drawNew = useSnackAction(async (name: string) => {
        await api.elevationTool.unlinkScreen({project, quoteOpening: props.row.id});
        navigate(`/project/${project}/quote/${props.alternative}/elevation-tool?quoteOpening=${props.row.id}&alternative=${props.alternative}&workspace=screen&autoSaveName=${encodeURIComponent(name.trim())}`);
    }, {success: "Unlinked — opening elevation tool"});

    if(list.loading || slots.loading) return <DropdownDataLoading anchor={props.anchor} width={props.width} />;
    if(list.error) return list.LoadingOrErrorElement;
    if(slots.error) return slots.LoadingOrErrorElement;

    // search matches a screen name, or the name of the opening that holds a frame.
    const options: Option[] = list.asList.map(s => ({value: "screen:" + s.id, display: s.name}));
    if(slots.asList.length > 0) {
        options.push({value: "slots", display: "Door slots on other openings' frames", subHeading: true});
        slots.asList.forEach((s, i) => options.push({value: "slot:" + i, display: `${s.primaryOpeningName} / ${s.slot} (${s.screenName})`}));
    }

    const select = (value: string, freeSolo?: boolean) => {
        if(freeSolo) return drawNew.callback(value);
        if(value.startsWith("slot:")) return fillSlot.callback(slots.asList[parseInt(value.slice("slot:".length))]);
        return adopt.callback(parseInt(value.slice("screen:".length)));
    };

    return (
        <SelectEditor
            anchor={props.anchor}
            options={options}
            value=""
            initialValue=""
            freeSolo
            onBlur={props.onCancel}
            onSelect={select}
        />
    );
}
